import { type FC, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useLocation } from 'react-router'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import {
	ArrowLeftIcon,
	Home03Icon,
	MarkerPin01Icon
} from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { useAuthStore } from '@/features/auth/store/store'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'
import { useClaimListing } from '../queries/use-claim-listing'
import { useSaveVisualVettingDraft } from '../queries/use-save-visual-vetting-draft'
import type { ScoreFormData, VisualVettingListingDetails } from '../types'
import { FULL_ACCESS_USER_ROLES, getReviewerName } from '../utils'
import { FinalScoreBadge } from './final-score-badge'
import { SaveDraftDialog } from './save-draft-dialog'
import { SourceBadge } from './source-badge'
import { SubmitConfirmationDialog } from './submit-confirmation-dialog'

interface ScoreFormHeaderProps {
	listingData: VisualVettingListingDetails
}

export const ScoreFormHeader: FC<ScoreFormHeaderProps> = ({ listingData }) => {
	const { t } = useTranslation()
	const form = useFormContext<ScoreFormData>()
	const user = useAuthStore((state) => state.user)
	const isAdmin = user?.role
		? FULL_ACCESS_USER_ROLES.includes(user.role)
		: false
	const location = useLocation()
	const [open, setOpen] = useState(false)
	const claimMutation = useClaimListing()
	const saveDraftMutation = useSaveVisualVettingDraft(listingData.id, {
		onSuccess: () => {
			form.reset(form.getValues(), { keepValues: true })
			toast.success(t('luxe_submissions:save_draft_success'))
		}
	})

	const submitAnswersMutation = useSaveVisualVettingDraft(listingData.id, {
		onSuccess: () => {
			setOpen(true)
		}
	})

	const isAssignedToMe = listingData.isAssignedToCurrentUser

	const handleClaim = () => {
		claimMutation.mutate([listingData.id])
	}

	const handleSubmit = form.handleSubmit((data) => {
		submitAnswersMutation.mutate({
			answers: Object.entries(data.answers)
				.filter(([, answer]) => !!answer)
				.map(([questionId, answer]) => ({
					questionId,
					answer
				}))
		})
	})

	return (
		<div className='border-neutral-200 border-b px-10 pt-6 pb-6'>
			<div className='flex items-center gap-6'>
				{form.formState.isDirty ? (
					<SaveDraftDialog
						getFormData={() => form.getValues()}
						listingId={listingData.id}
						trigger={
							<ButtonLegacy
								className='size-11 shrink-0 px-0!'
								shape='rounded'
								type='button'
								variant='icon'
							>
								<ArrowLeftIcon size={20} />
							</ButtonLegacy>
						}
					/>
				) : (
					<ButtonLinkLegacy
						className='size-11 shrink-0 px-0!'
						shape='rounded'
						to={{
							pathname:
								routes.LUXE_SUBMISSIONS_QUEUE_VISUAL_VETTING
									.handle.fullPath,
							search: location.state?.fromSearch
						}}
						variant='icon'
					>
						<ArrowLeftIcon size={20} />
					</ButtonLinkLegacy>
				)}

				<div className='flex flex-col gap-2 text-neutral-900'>
					<h1 className='font-bold text-lg'>
						{t('luxe_submissions:visual_vetting_for')}
					</h1>

					<div className='flex items-center gap-6 text-neutral-500'>
						<span className='flex items-center gap-2 font-medium text-lg'>
							<Home03Icon size={24} />
							<a
								className={
									listingData.listingUrl
										? 'underline'
										: undefined
								}
								href={listingData.listingUrl ?? undefined}
								rel='noopener noreferrer'
								target='_blank'
							>
								#{listingData.airbnbListingId ?? listingData.id}
							</a>
						</span>

						{listingData.location && (
							<span className='flex items-center gap-2 text-base'>
								<MarkerPin01Icon size={24} />
								{listingData.location}
							</span>
						)}

						<SourceBadge source={listingData.source} />

						{listingData.vettingStepStatus === 'completed' && (
							<FinalScoreBadge
								blockersFailedCount={
									listingData.blockersFailedCount
								}
								designScore={listingData.score}
								finalScore={listingData.finalScore}
								hasBreakdownTooltip={false}
								hasScoreFallback={false}
							/>
						)}
					</div>
				</div>

				<div className='ml-auto'>
					{!listingData.vetter && (
						<ButtonLegacy
							disabled={claimMutation.isPending}
							onClick={handleClaim}
							type='button'
							variant='tertiary'
						>
							{t('luxe_submissions:claim')}
							{claimMutation.isPending && <Spinner />}
						</ButtonLegacy>
					)}
					{isAssignedToMe &&
						listingData.vettingStepStatus === 'in_progress' && (
							<div className='flex gap-2'>
								<ButtonLegacy
									disabled={saveDraftMutation.isPending}
									onClick={() =>
										saveDraftMutation.mutate({
											answers: Object.entries(
												form.getValues().answers
											).map(
												([questionId, answer]: [
													string,
													string | null
												]) => ({
													questionId,
													answer: answer ?? null
												})
											)
										})
									}
									type='button'
									variant='tertiary'
								>
									{t('luxe_submissions:save_draft')}
									{saveDraftMutation.isPending && <Spinner />}
								</ButtonLegacy>

								<ButtonLegacy
									disabled={submitAnswersMutation.isPending}
									onClick={handleSubmit}
									type='button'
									variant='brand'
								>
									{t('common:submit')}
									{submitAnswersMutation.isPending && (
										<Spinner />
									)}
								</ButtonLegacy>
							</div>
						)}
					{!!listingData.vetter && !isAssignedToMe && !isAdmin && (
						<span className='font-semibold text-neutral-900 text-sm'>
							{t('luxe_submissions:view_only')}
						</span>
					)}

					{isAdmin &&
						listingData.vetter &&
						!isAssignedToMe &&
						(listingData.vettingStepStatus !== 'completed' ? (
							<div className='flex flex-col items-end'>
								<p className='flex items-center gap-2 font-medium text-neutral-900 text-sm'>
									{t('luxe_submissions:assign_to')}
									<span className='font-normal text-neutral-600 text-sm'>
										{
											getReviewerName(listingData.vetter)
												.fullName
										}
									</span>
								</p>
								<ButtonLegacy
									className='px-0'
									disabled={claimMutation.isPending}
									onClick={handleClaim}
									type='button'
									variant='tertiary'
								>
									{t('luxe_submissions:claim')}
									{claimMutation.isPending && <Spinner />}
								</ButtonLegacy>
							</div>
						) : (
							<span className='font-semibold text-neutral-900 text-sm'>
								{t('luxe_submissions:view_only')}
							</span>
						))}
				</div>
			</div>

			<SubmitConfirmationDialog
				listingId={listingData.id}
				onOpenChange={setOpen}
				open={open}
			/>
		</div>
	)
}
